import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ConsumptionsBillsSearchBar from "../../ConsumptionsBillsSearchBar/ConsumptionsBillsSearchBar";
import { useContext, useEffect, useRef, useState } from "react";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import {
  ConsumptionListData,
  GetGraphDataRequestBody,
  HourConsumption,
  HourConsumptionFormatted,
  SelectedDataPeriodConsumptions,
} from "../../../core/models/consumptions/consumptions.model";
import { getGraphDataHourlyConsumptions } from "../../../core/services/consumptions/consumptions.service";
import { SearchParams } from "../../../core/models/consumptionsBillsSearchBar/consumptionsBillsSearchBar.model";
import { useTranslation } from "react-i18next";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
import EmptyStateComponent from "../../UI/EmptyStateComponent/EmptyStateComponent";
import theme from "../../../muiTheme";
import {
  formatHourlyConsumptions,
  getHourlyBarChart,
} from "../../../utils/components/consumptions/consumptions.util";
import { OwnerDataCups } from "../../../core/models/ownerData/ownerData.model";
import { ActiveElement, Chart, ChartEvent } from "chart.js";
import { formatNumber } from "../../../utils/formatters/formatters.util";
import {
  formatDate,
  formatStringDate,
} from "../../../utils/dateUtils/formatDate.util";
import { CustomDateError } from "../../../core/models/datePickersComponent/datePickersComponent.model";
import { ChangeEventDatePicker } from "../../../core/models/bills/bills.model";

interface ConsumptionsHourlyProps {
  searchParams: SearchParams;
  setSearchParams: React.Dispatch<React.SetStateAction<SearchParams>>;
  setHourlyConsumptionsList: React.Dispatch<
    React.SetStateAction<HourConsumptionFormatted[]>
  >;
  hourlyConsumptionsList: HourConsumptionFormatted[];
  ownerDataCups: OwnerDataCups[];
  selectedDataPeriod: SelectedDataPeriodConsumptions;
  setSelectedDataPeriod: React.Dispatch<
    React.SetStateAction<SelectedDataPeriodConsumptions>
  >;
}

export default function ConsumptionsHourly({
  searchParams,
  setSearchParams,
  hourlyConsumptionsList,
  setHourlyConsumptionsList,
  ownerDataCups,
}: ConsumptionsHourlyProps) {
  const { ownerData, userData } = useContext(TotalEnergiesContext);

  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isDesktopViewport = useMediaQuery("(min-width: 1100px )");
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
  const [isFirstChartLoadFlag, setIsFirstChartLoadFlag] =
    useState<boolean>(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chart = useRef<Chart | null>(null);
  const [environment, setEnvironment] = useState<"Gas" | "Luz">("Luz");

  const datasetLabels = [t("Consumos.Activa"), t("Consumos.Reactiva")];

  useEffect(() => {
    if (canvasRef.current) {
      chart.current = getHourlyBarChart(
        environment,
        canvasRef.current,
        hourlyConsumptionsList,
        isDesktopViewport,
        datasetLabels
      );

      return () => {
        (chart.current as Chart).destroy();
      };
    }
  }, [hourlyConsumptionsList, isDesktopViewport]);

  const consumptionsPeriodInformation = {
    totalActiveEnergy: hourlyConsumptionsList
      .map(({ activeEnergy }) => activeEnergy)
      .reduce((sum, num) => sum + num, 0),
    totalReactiveEnergy: hourlyConsumptionsList
      .map(({ reactiveEnergy }) => reactiveEnergy)
      .reduce((sum, num) => sum + num, 0),
  };

  const consumptionListData: ConsumptionListData[] = [
    {
      id: "selectedPeriod",
      label: "Periodo seleccionado",
      value: `${
        searchParams.dateFrom
          ? formatDate(new Date(searchParams.dateFrom.$d))
          : "-"
      } - ${
        searchParams.dateTo ? formatDate(new Date(searchParams.dateTo.$d)) : "-"
      }`,
    },
    {
      id: "totalActiveEnergy",
      label: "Total de energía activa",
      value: `${formatNumber(
        consumptionsPeriodInformation.totalActiveEnergy
      )} ${environment === "Gas" ? "m3" : "kWh"}`,
    },
    {
      id: "totalConsumedEnergy",
      label: "Total de energía reactiva",
      value: `${formatNumber(
        consumptionsPeriodInformation.totalReactiveEnergy
      )} ${environment === "Gas" ? "m3" : "kWh"}`,
    },
  ];

  useEffect(() => {
    if (
      isFirstChartLoadFlag &&
      chart.current &&
      hourlyConsumptionsList.length
    ) {
      setIsFirstChartLoadFlag(false);
      return;
    }

    if (!isFirstChartLoadFlag && !hourlyConsumptionsList.length) {
      setIsFirstChartLoadFlag(true);
    }
  }, [isFirstChartLoadFlag, hourlyConsumptionsList, chart]);

  const handleClickSearch = (searchParams: GetGraphDataRequestBody) => {
    setIsLoading(true);
    getGraphDataHourlyConsumptions(searchParams, userData?.token || "")
      .then((result) => {
        setHourlyConsumptionsList(formatHourlyConsumptions(result));

        const cupsId = searchParams.cupsId;
        const cupsEnvironment = ownerDataCups.find(
          (cups) => cups.idCups === cupsId
        )?.envirorment;
        setEnvironment(cupsEnvironment === "Gas" ? "Gas" : "Luz");
        setIsSearchActive(true);
      })
      .catch((error) => {
        console.error("Error fetching hourly consumptions:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const customDateError: CustomDateError[] = [
    {
      errorType: "maxDate",
      text: t(
        "Datepicker.El intervalo de fechas no puede ser superior a seis meses"
      ),
    },
    {
      errorType: "minDate",
      text: t(
        "Datepicker.La fecha inicial no puede ser posterior a la fecha final"
      ),
    },
  ];

  return (
    <Stack
      component="section"
      rowGap={"2.5rem"}
      padding={{
        xs: "1rem 0",
        md: "40px 0",
      }}
    >
      <ConsumptionsBillsSearchBar
        ownerDataCups={ownerData?.cups || []}
        handleClickSearch={handleClickSearch}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        isLoading={isLoading}
        isHourlySearch={true}
        isFirstChartLoadFlag={isFirstChartLoadFlag}
        customDateError={customDateError}
        maxDateTo={6}
        isChartLoad
      />
      {!isLoading ? (
        <>
          {!!hourlyConsumptionsList.length ? (
            <Paper sx={{ width: "100%", borderRadius: "12px" }}>
              <Box
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                borderBottom={`solid 2px ${theme.palette.grey[100]}`}
                paddingX="2rem"
                paddingY="1.5rem"
                gap="4rem"
              >
                {consumptionListData.map(({ id, label, value }) => (
                  <Stack direction="column" gap="0.5rem" key={id}>
                    <Typography
                      component="p"
                      variant="body2"
                      sx={{ color: "grey.500" }}
                    >
                      {t(`Consumos.${label}`)}
                    </Typography>
                    <Typography component="h3" variant="subtitle1">
                      {value}
                    </Typography>
                  </Stack>
                ))}
              </Box>
              <Stack component="div">
                <Box sx={{ paddingBottom: "24px" }}>
                  <canvas
                    id="chart"
                    ref={canvasRef}
                    style={{ height: "600px" }}
                  />
                </Box>
              </Stack>
            </Paper>
          ) : (
            <>
              {isSearchActive ? (
                <EmptyStateComponent
                  text={
                    "Consumos.Selecciona otro periodo para mostrar consumos"
                  }
                  height={"30rem"}
                />
              ) : (
                <EmptyStateComponent
                  text={
                    "Consumos.Selecciona un suministro y un periodo para mostrar consumos"
                  }
                  height={"30rem"}
                />
              )}
            </>
          )}
        </>
      ) : (
        <Paper
          sx={{
            width: "100%",
            borderRadius: "12px",
            height: "30rem",
            position: "relative",
          }}
        >
          <LoadingComponentComponent isLoading={isLoading} />
        </Paper>
      )}
    </Stack>
  );
}
